import { cn } from '@heroui/react';
import { useAsyncList } from '@react-stately/data';
import React, { Key, useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { format } from 'date-fns';
import { MatchTag, QuoteStatusTag } from 'src/shared/components';
import { ROUTE_PATHS } from 'src/shared/config/routes';
import { ProductById } from 'src/shared/types/product';
import { formatMoney } from 'src/shared/utils/formatMoney';
import isValidDate from 'src/shared/utils/isValidDate';
import {
  pricingUnitPriceSuffix,
  pricingUnitQuantitySuffix
} from 'src/shared/utils/pricingUnit';

import StaffOnlyTag, {
  STAFF_ONLY_ROW_CLASSNAME
} from 'src/shared/components/StaffOnlyTag';
import {
  SortDescriptor,
  Spinner,
  useDisclosure
} from 'src/shared/components/ui';
import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Typography
} from 'src/shared/components/ui';

import quotesColumns from '../../constants/quotesColumns';
import { CountryFlag } from './CountryFlag';
import QuoteDetailModal from './QuoteDetailModal';

interface QuotesTableProps {
  quotes: any[];
  product: ProductById;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  selectedSupplier: string;
}

const getQuotesByFilter = (quotes: any[], filterText: string | undefined) => {
  if (!filterText) return quotes;
  if (filterText === 'all') return quotes;

  return quotes?.filter((quote: any) => {
    return quote?.supplier?.cloak === filterText;
  });
};

const QuotesTable: React.FC<QuotesTableProps> = ({
  quotes,
  product,
  isLoading,
  setIsLoading,
  selectedSupplier
}) => {
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [selectedQuote, setSelectedQuote] = useState<any>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const quoteId = searchParams.get('quote');

  useEffect(() => {
    if (quotes.length > 0 && quoteId) {
      const quote = quotes.find((quote) => `${quote.id}` === quoteId);

      if (quote) {
        setSelectedQuote(quote);
        if (!isOpen) {
          onOpen();
        }
      }
    } else if (!quoteId && isOpen) {
      onOpenChange();
    }
  }, [quotes, quoteId, isOpen, onOpen, onOpenChange]);

  const handleModalClose = (newIsOpen: boolean) => {
    if (!newIsOpen && quoteId) {
      const newSearchParams = new URLSearchParams(searchParams);

      newSearchParams.delete('quote');
      setSearchParams(newSearchParams, { replace: true });
    }
    if (newIsOpen) {
      onOpen();
    } else {
      onOpenChange();
    }
  };

  const list = useAsyncList({
    async load() {
      setIsLoading(false);
      const filteredQuotes = getQuotesByFilter(quotes, selectedSupplier);

      return {
        items: filteredQuotes
      };
    },
    async sort({ items, sortDescriptor, filterText }) {
      if (!sortDescriptor.direction) {
        const filteredQuotes = getQuotesByFilter(quotes, filterText);

        return {
          items: [...filteredQuotes]
        };
      }

      return {
        items: items.sort((a: any, b: any) => {
          let first = a[sortDescriptor.column];
          let second = b[sortDescriptor.column];

          if (first == null) return 1;
          if (second == null) return -1;

          let cmp: number;

          if (
            typeof first === 'string' &&
            typeof second === 'string' &&
            isValidDate(first) &&
            isValidDate(second)
          ) {
            cmp = new Date(first).getTime() - new Date(second).getTime();
          } else {
            const firstNum = Number(first);
            const secondNum = Number(second);

            if (!isNaN(firstNum) && !isNaN(secondNum)) {
              cmp = (firstNum || 0) - (secondNum || 0);
            } else {
              cmp = String(first).localeCompare(String(second));
            }
          }

          if (sortDescriptor.direction === 'descending') {
            cmp *= -1;
          }

          return cmp;
        })
      };
    }
  });

  useEffect(() => {
    list.setFilterText(selectedSupplier);
  }, [selectedSupplier, quotes]);

  const handleSortChange = (descriptor: SortDescriptor) => {
    if (
      list.sortDescriptor?.column === descriptor.column &&
      list.sortDescriptor?.direction === 'descending'
    ) {
      list.sort({ column: undefined, direction: undefined } as any);
    } else {
      list.sort(descriptor);
    }
  };

  const renderCell = useCallback((item: any, columnKey: Key) => {
    switch (columnKey) {
      case 'variant':
        return (
          <div className="flex items-center gap-2">
            <Typography variant="body-2" weight="medium">
              {item.variant ?? '-'}
            </Typography>
            {item.staffOnly && <StaffOnlyTag />}
          </div>
        );

      case 'price':
        return (
          <Typography variant="body-2" weight="medium">
            {item.price
              ? `${formatMoney(item.price)}${pricingUnitPriceSuffix(item.pricing_unit)}`
              : '-'}
          </Typography>
        );

      case 'quantity':
        return (
          <Typography variant="body-2">
            {item.quantity != null
              ? `${item.quantity}${pricingUnitQuantitySuffix(item.pricing_unit)}`
              : '-'}
          </Typography>
        );

      case 'supplier_name':
        return <Typography variant="body-2">{item.supplier_name}</Typography>;

      case 'match_score':
        return (
          <MatchTag score={item?.match_score} recommended={item?.recommended} />
        );

      case 'ui_status':
        return <QuoteStatusTag quote={item} />;

      case 'updated_at':
        return (
          <Typography variant="body-2">
            {format(item.updated_at, 'MMM dd, yyyy')}
          </Typography>
        );

      case 'supplier_factory_country':
        return (
          <div className="flex items-center gap-1 mx-auto relative left-2">
            <CountryFlag countryCode={item.supplier_factory_country} />
          </div>
        );

      default:
        return '';
    }
  }, []);

  return (
    <div className="flex flex-col">
      <Table
        aria-label="Quotes table"
        sortDescriptor={list?.sortDescriptor}
        onSortChange={handleSortChange}
      >
        <TableHeader columns={quotesColumns}>
          {(column) => (
            <TableColumn allowsSorting className="px-4" key={column.key}>
              {column.label}
            </TableColumn>
          )}
        </TableHeader>
        <TableBody
          emptyContent={
            isLoading ? (
              <Spinner size="lg" />
            ) : (
              list?.items?.length === 0 && 'No quotes'
            )
          }
          items={list?.items ?? []}
          isLoading={isLoading}
        >
          {(item) => (
            <TableRow
              key={item.id}
              className={cn(item.staffOnly && STAFF_ONLY_ROW_CLASSNAME)}
            >
              {(columnKey) => (
                <TableCell>
                  <Link
                    className="h-16 flex items-center"
                    to={`${ROUTE_PATHS.PRODUCTS}/${product.id}?tab=quotes&quote=${item.id}`}
                  >
                    <div className="truncate">
                      {renderCell(item, columnKey)}
                    </div>
                  </Link>
                </TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>

      <QuoteDetailModal
        isOpen={isOpen}
        onOpenChange={handleModalClose}
        selectedQuote={selectedQuote}
      />
    </div>
  );
};

export default QuotesTable;
